import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Message, User } from '../../api/types';
import { todayISO } from '../../utils/time';
import { useToast } from '../../components/Toast';
import { AsyncButton } from '../../components/AsyncButton';

function fmtDT(s: string) {
  return new Intl.DateTimeFormat('it-IT', { dateStyle:'short', timeStyle:'short' }).format(new Date(s));
}

export function AdminMessagesPage() {
  const { push } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [userId, setUserId] = useState('');
  const [items, setItems] = useState<Message[]>([]);
  const [workDate, setWorkDate] = useState(() => todayISO());
  const [body, setBody] = useState('');

  async function loadUsers() {
    const r = await api.get<{users: User[]}>('/api/admin/users');
    setUsers(r.users);
    if (!userId && r.users[0]) setUserId(r.users[0]._id);
  }

  async function load() {
    const r = await api.get<{items: Message[]}>(userId ? `/api/messages?withUserId=${userId}` : '/api/messages');
    setItems(r.items);
  }

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { load(); }, [userId]);

  async function send() {
    if (!userId || !body.trim()) return;
    try {
      await api.post('/api/messages', { to_user_id: userId, work_date: workDate, body: body.trim() });
      setBody('');
      push({ title: 'Messaggio inviato' });
      await load();
    } catch (e:any) {
      push({ title: 'Errore invio', body: e?.data?.error || e?.message });
    }
  }

  const ordered = useMemo(() => items.slice().sort((a,b) => +new Date(a.sent_at) - +new Date(b.sent_at)), [items]);

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="card">
        <div className="h2" style={{ marginTop:0 }}>Messaggi</div>

        <div className="grid" style={{ gridTemplateColumns:'1fr 1fr', gap: 10 }}>
          <div>
            <div className="small">Dipendente</div>
            <select className="input" value={userId} onChange={(e) => setUserId(e.target.value)}>
              {users.map(u => <option key={u._id} value={u._id}>{u.first_name} {u.last_name}</option>)}
            </select>
          </div>
          <div>
            <div className="small">Giorno</div>
            <input className="input" type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="small">Testo</div>
          <textarea className="input" style={{ minHeight: 110 }} value={body} onChange={(e) => setBody(e.target.value.slice(0, 500))} />
        </div>

        <AsyncButton className="btn btnPrimary" style={{ marginTop: 10 }} onClickAsync={send} disabled={!userId || !body.trim()} busyText="Invio…">Invia</AsyncButton>
      </div>

      <div className="card">
        <div className="spread">
          <div className="h3" style={{ margin:0 }}>Thread</div>
          <AsyncButton className="btn btnSecondary" onClickAsync={load} busyText="Aggiorno…">Aggiorna</AsyncButton>
        </div>

        <div style={{ marginTop: 12, display:'grid', gap: 10 }}>
          {ordered.map(m => (
            <div key={m._id} className="card" style={{ boxShadow:'none', background:'var(--surface-muted)' }}>
              <div className="small">{fmtDT(m.sent_at)}</div>
              <div style={{ marginTop: 6, fontWeight: 700 }}>{m.body}</div>
            </div>
          ))}
          {ordered.length === 0 ? <div className="small">Nessun messaggio.</div> : null}
        </div>
      </div>
    </div>
  );
}
